import { useContext, useEffect, useCallback } from 'react';
import { Box, Text, Card, Palette } from '@updater/ui-uds';
import { PageHeading } from '@updater/ui-widgets';
import styled from '@emotion/styled';
import { BackButton } from 'components/BackButton';
import router from 'next/router';
import { createLead, createLeadVariables } from 'types/generated/createLead';
import { useMutation } from '@apollo/client';
import { useTracking } from 'react-tracking';
import { createLeadMutation } from 'services/apollo/mutations';
import { useQual, useSessionStorage } from 'hooks';
import { resolveEmbeddingOrigin } from 'utils/whitelist/resolveEmbeddingOrigin';
import { reportError } from 'services/datadog/data-dog-rum';
import { MicrositeContext } from 'context';
import { Content } from './Content';
import { LeadModeForm } from './LeadModeForm';
import { Aside } from './Aside';
// TODO: constants folder should be referenceable as a root-level path...
import { PageRoutes, providerAppSessionStorageKey } from '../../constants';
import { buildOptionalFields } from 'utils/buildOptionalFields';
import { CreateLeadInput, OfferInput } from '@updater/consumer-graph';
import { buildPlanName } from 'utils/buildPlanName';
import { setAttributes } from '@updater/identity';

export const Main = styled(Box)`
  max-width: 708px;
`;

export const Page = styled(Box)`
  max-width: 1265px;
  margin: 0 auto;
`;

export const DisclaimerCard = styled(Card)`
  border-color: ${Palette.blue200};
  background-color: ${Palette.blue50};
`;

export function LeadModePage() {
  const { micrositeCode } = useContext(MicrositeContext);
  const qual = useQual();
  const { promoId, uuid, userEmail } = qual;
  const optionalFields = buildOptionalFields(qual);
  const [createLead] = useMutation<createLead, createLeadVariables>(
    createLeadMutation
  );
  const [sessionStorageValue, setSessionStorageValue] = useSessionStorage(
    providerAppSessionStorageKey,
    {}
  );
  const { trackEvent } = useTracking({
    object: 'lead_mode_info',
    details: {
      // TODO: what do we want this value to represent?
      provider: micrositeCode,
    },
  });

  // Track view event after mount (empty dep array to run once)
  useEffect(() => {
    trackEvent({
      verb: 'viewed',
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = useCallback(
    async (values) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { firstName, lastName, email, phone } = values;

      const requestId = sessionStorageValue?.requestId;
      if (!requestId) {
        router.push(PageRoutes.leadModeError);
        return;
      }

      // The form forces a leading +1 on all phone numbers.  However,
      // serviceability and DC expect a 10-digit number.  If we pass
      // 11 digits, they will truncate and we'll lose the last digit.
      // Make sure the phone number is only 10 digits here:
      const tenDigitPhone =
        phone.length > 10 ? phone.substring(phone.length - 10) : phone;
      try {
        const response = await createLead({
          variables: {
            input: {
              requestId,
              promoId,
              optionalFields,
              partner: {
                user: {
                  uuid,
                  email: userEmail,
                },
              },
              offer: {
                name: sessionStorageValue?.offer?.planName,
                code: sessionStorageValue?.offer?.code,
                providerCode: sessionStorageValue?.offer?.providerCode,
                pricing: sessionStorageValue?.offer?.pricing,
                products: sessionStorageValue?.offer?.products,
                providerOfferId: sessionStorageValue?.offer?.providerOfferId,
                metadata: sessionStorageValue?.offer?.metadata,
              } as OfferInput,
              customerInformation: {
                firstName,
                lastName,
                email,
                phone: tenDigitPhone,
              },
            } as CreateLeadInput,
          },
        });

        if (response?.data?.createLead?.leadId?.length) {
          setAttributes({
            firstName,
            lastName,
            email,
            leadId: response.data.createLead.leadId,
          });
          setSessionStorageValue({
            ...sessionStorageValue,
            customerInformation: {
              firstName,
              lastName,
              email,
              phone,
              address: response?.data?.createLead?.address,
            },
            confirmationNumber: response?.data?.createLead?.leadId,
            confirmationPhoneNumber:
              response?.data?.createLead?.confirmationPhoneNumber,
            orderDate: new Date().toLocaleDateString(),
          });
          trackEvent({
            verb: 'submitted',
            details: {
              email,
              phone,
              // A lead is a conversion against an offer the visitor picked on
              // the plans page, and the merchandising context of that pick
              // (section, position, pill) lives on the `offers.submitted`
              // event there. Carry the keys needed to join back to it rather
              // than restating the context, which would drift. `requestId`
              // alone is ambiguous when a visitor selects more than one offer
              // in a single serviceability request, so the offer code goes
              // with it.
              requestId,
              offerCode: sessionStorageValue?.offer?.code,
            },
          });
          // The lead is saved by this point, so nothing below should keep the
          // visitor from their confirmation page — hence the inner catch.
          try {
            // WhitelistGuard has already vetted the embedding chain.
            const embedding = resolveEmbeddingOrigin();

            if (embedding.state === 'embedded') {
              window.parent.postMessage(
                {
                  object: 'lead',
                  verb: 'submitted',
                  details: {
                    confirmationNumber: response?.data?.createLead?.leadId,
                    planName: buildPlanName({
                      offerName: sessionStorageValue?.offer?.planName,
                      providerName: sessionStorageValue?.offer?.providerName,
                      providerId: sessionStorageValue?.offer?.providerCode,
                      speedMbps:
                        sessionStorageValue?.offer?.products?.internet
                          ?.downloadSpeed?.value,
                      isTransfer: false,
                    }),
                  },
                },
                embedding.ancestors[0] // always available if state is embedded
              );
            } else if (embedding.state === 'undeterminable') {
              // Skipped, rather than broadcast to '*'
              reportError(
                new Error(
                  'lead-submitted postMessage skipped: embedding origin undeterminable'
                )
              );
            }
          } catch (notifyError) {
            // Worth tracking errors here, but needs additional context to
            // prevent any alarm - just a postMessage error, not a lead error
            reportError(notifyError as Error, {
              notification: 'lead-submitted',
              leadSaved: true, // to prevent false alarm about leads failing
            });
          }

          router.push(PageRoutes.leadModeConfirmation);
        } else {
          router.push(PageRoutes.leadModeError);
        }
      } catch (error) {
        router.push(PageRoutes.leadModeError);
      }
    },
    [router, createLead]
  );
  return (
    <Page>
      <Box marginTop="xl">
        <BackButton />
      </Box>
      <Box marginY="m">
        <PageHeading>Order in a few easy steps!</PageHeading>
      </Box>
      <Content>
        <Main>
          <LeadModeForm handleChange={() => {}} onSubmit={onSubmit} />
          <Box mt={['xxs', null, 'xs', null, null]}>
            <DisclaimerCard variant="squared" borderColor="light">
              <Text variant="s">
                By clicking “Submit”, I agree to allow order and installation
                information to be delivered via email and/or a recorded phone
                line.
              </Text>
            </DisclaimerCard>
          </Box>
          <Box my={['m', null, 'l', null, null]}>
            <DisclaimerCard variant="squared">
              <Text variant="s">
                YOU WILL BE ABLE TO SELECT EQUIPMENT, MODIFY ORDER, and schedule
                installation during the call to complete your order.
                Programming, pricing and offers are subject to change and may
                vary in certain markets. Some offers may not be available
                through all channels and in select areas.
              </Text>
            </DisclaimerCard>
          </Box>
        </Main>
        <Aside />
      </Content>
    </Page>
  );
}
